<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div class="compliance-item">
    <div class="compliance-item-left">
      <div class="compliance-item-icon" :class="iconClass">
        <svg v-if="isDone" viewBox="0 0 12 12" fill="none" stroke-width="2.5" stroke-linecap="round"><path d="M2 6l3 3 5-5" stroke="currentColor"/></svg>
        <svg v-else viewBox="0 0 12 12" fill="none" stroke-width="2.5" stroke-linecap="round"><path d="M6 3v4M6 9v1" stroke="currentColor"/></svg>
      </div>
      <div style="flex:1;min-width:0;">
        <div class="compliance-item-title">{{ label }}</div>

        <!-- VIEW mode -->
        <div v-if="!editing" class="compliance-item-label">
          <span v-if="value">{{ value }}</span>
          <span v-else class="muted">{{ fallback || 'Awaiting owner' }}</span>
        </div>

        <!-- EDIT mode -->
        <div v-else class="compliance-item-edit">
          <template v-if="inputType === 'address-group'">
            <input type="text" v-model="form.address_line_1" placeholder="Address line 1"/>
            <input type="text" v-model="form.address_line_2" placeholder="Address line 2 (optional)"/>
            <div class="edit-grid">
              <input type="text" v-model="form.city" placeholder="City"/>
              <input type="text" v-model="form.postal_code" placeholder="Postcode"/>
            </div>
          </template>
          <template v-else-if="inputType === 'select-country'">
            <select v-model="form.country">
              <option value="GB">United Kingdom</option>
              <option value="US">United States</option>
              <option value="IE">Ireland</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="OTHER">Other</option>
            </select>
          </template>
          <template v-else-if="inputType === 'select-ownership'">
            <select v-model="form.ownership_type">
              <option value="">—</option>
              <option value="personal">I own this property personally</option>
              <option value="company">Through a limited company</option>
              <option value="trust">Through a trust</option>
            </select>
          </template>
          <template v-else-if="inputType === 'tel'">
            <input type="tel" v-model="form.phone_number" placeholder="+44 …"/>
          </template>
          <template v-else-if="inputType === 'tax-group'">
            <input type="text" v-model="form.utr_number" placeholder="UTR (10 digits)"/>
            <input type="text" v-model="form.national_insurance_number" placeholder="National Insurance number"/>
          </template>
          <template v-else-if="inputType === 'file'">
            <input type="file" @change="onFile" accept="application/pdf,image/*"/>
            <span class="edit-hint">Passport or driving licence · PDF / JPG / PNG</span>
          </template>
          <template v-else-if="inputType === 'date'">
            <input type="date" v-model="form.date_of_birth"/>
          </template>
          <template v-else-if="inputType === 'email'">
            <input type="email" v-model="form.primary_email"/>
          </template>
          <template v-else>
            <input type="text" v-model="form[fieldName]"/>
          </template>

          <div class="edit-actions">
            <button class="btn-mini btn-mini-outline" @click="cancel">Cancel</button>
            <button class="btn-mini btn-mini-primary" @click="save">Done</button>
          </div>
        </div>
      </div>
    </div>

    <div class="compliance-item-right">
      <span v-if="future" class="future-tag">future</span>
      <span v-else class="pill pill-mini" :class="pillClass">{{ pillLabel }}</span>
      <button v-if="!future && !editing" class="edit-btn" @click="$emit('edit')" aria-label="Edit">
        <svg viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
      </button>
    </div>
  </div>
</template>

<script>
/* eslint-disable vue/no-mutating-props */
export default {
  name: 'ComplianceItem',
  props: {
    label: { type: String, required: true },
    value: { type: [String, Number], default: '' },
    fallback: { type: String, default: '' },
    editing: { type: Boolean, default: false },
    fieldName: { type: String, default: '' },
    inputType: { type: String, default: 'text' },
    form: { type: Object, default: () => ({}) },
    future: { type: Boolean, default: false },
  },
  emits: ['edit', 'save', 'cancel', 'file-change'],
  computed: {
    isDone() {
      return !!this.value && !this.future
    },
    iconClass() {
      if (this.future) return 'missing'
      return this.isDone ? 'done' : 'missing'
    },
    pillClass() {
      return this.isDone ? 'pill-green' : 'pill-amber'
    },
    pillLabel() {
      return this.isDone ? 'Complete' : 'Awaiting owner'
    },
  },
  methods: {
    save() {
      this.$emit('save')
    },
    cancel() {
      this.$emit('cancel')
    },
    onFile(e) {
      const f = e.target.files && e.target.files[0]
      if (f) this.$emit('file-change', f)
    },
  },
}
</script>

<style scoped>
.compliance-item { display: flex; align-items: flex-start; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #f0eef8; gap: 12px; }
.compliance-item:last-child { border-bottom: none; }
.compliance-item-left { display: flex; align-items: flex-start; gap: 10px; flex: 1; min-width: 0; }
.compliance-item-icon { width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
.compliance-item-icon.done { background: rgba(0,184,148,0.1); border: 1.5px solid rgba(0,184,148,0.25); color: #00b894; }
.compliance-item-icon.missing { background: rgba(253,203,110,0.2); border: 1.5px solid rgba(253,203,110,0.5); color: #b7791f; }
.compliance-item-icon svg { width: 11px; height: 11px; fill: none; stroke-width: 2.5; stroke-linecap: round; }
.compliance-item-title { font-size: 13px; color: #1a1a2e; font-weight: 500; }
.compliance-item-label { color: #9896bb; font-size: 11px; margin-top: 2px; word-break: break-word; }
.compliance-item-label .muted { color: #9896bb; font-style: italic; }
.compliance-item-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

.pill-mini { font-size: 10px; padding: 2px 8px; }
.pill { display: inline-flex; align-items: center; gap: 4px; border-radius: 20px; font-weight: 600; white-space: nowrap; }
.pill::before { content: ''; width: 5px; height: 5px; border-radius: 50%; background: currentColor; opacity: 0.7; }
.pill-green { background: rgba(0,184,148,0.1); color: #00b894; border: 1px solid rgba(0,184,148,0.25); }
.pill-amber { background: rgba(253,203,110,0.2); color: #b7791f; border: 1px solid rgba(253,203,110,0.5); }

.future-tag { font-size: 9px; font-weight: 700; padding: 1px 6px; border-radius: 20px; background: rgba(245,158,11,0.1); color: #92400e; border: 1px solid rgba(245,158,11,0.3); text-transform: uppercase; letter-spacing: 0.06em; }

.edit-btn { background: transparent; border: none; padding: 4px; cursor: pointer; border-radius: 5px; color: #9896bb; transition: color 0.12s, background 0.12s; }
.edit-btn:hover { color: #5b4fcf; background: rgba(91,79,207,0.08); }
.edit-btn svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

.compliance-item-edit { display: flex; flex-direction: column; gap: 6px; margin-top: 6px; }
.compliance-item-edit input[type=text], .compliance-item-edit input[type=email], .compliance-item-edit input[type=date], .compliance-item-edit input[type=file], .compliance-item-edit select {
  border: 1.5px solid #e8e6f4; border-radius: 7px; padding: 7px 10px; font-size: 12px; color: #1a1a2e;
  font-family: 'Inter', sans-serif; background: #fff; outline: none; width: 100%;
}
.compliance-item-edit input:focus, .compliance-item-edit select:focus { border-color: #5b4fcf; box-shadow: 0 0 0 2px rgba(91,79,207,0.08); }
.compliance-item-edit select { -webkit-appearance: none; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%239896bb'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; background-size: 8px; padding-right: 28px; }
.edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.edit-hint { font-size: 10px; color: #9896bb; }
.edit-actions { display: flex; gap: 6px; justify-content: flex-end; margin-top: 4px; }
.btn-mini { padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; border: none; font-family: 'Inter', sans-serif; }
.btn-mini-primary { background: #5b4fcf; color: #fff; }
.btn-mini-outline { background: transparent; color: #4a4880; border: 1.5px solid #e8e6f4; }
</style>
